<script>
	// NPM Modules
	import dayjs from 'dayjs';
	import { createEventDispatcher } from 'svelte';
	// Stores
	import PriceStore from '@/Stores/Racing/PriceStore';
	import RaceStore from '@/Stores/Racing/RaceStore';
	import { showBetsForSelectionId } from '@/Stores/Store';
	// Constants
	import { BetType } from '@/Constants/Bet';
	import { Products } from '@/Constants/RaceProducts';
	import { SelectionStatus } from '@/Constants/Selection';
	import { EventStatus } from '@/Constants/Event';
	// Partials
	import SelectionBets from '@/Partials/Racing/Selections/SelectionBets.svelte';
	import PriceRoll from '@/Partials/Racing/Selections/_PriceRoll.svelte';
	import OfficialFlucs from '@/Partials/Racing/Selections/_OfficialFlucs.svelte';
	// Components
	import AnimateChange from '@/Components/AnimateChange.svelte';
	import Icon from '@/Components/Icon.svelte';
	// Services
	import { NumberFormat } from '@/Services/Helpers';
	import { getGetOrdinal } from '@/Services/Helpers.js';

	export let selection;
	export let favourite;
	export let includeMulti = true;
	export let previewDivisor = false;
	export let divisor;
	export let notValidDivisor = false;
	export let selectionToScratch = null;
	export let selectionToUnscratch = null;
	export let selectionToUpdate = null;
	export let selectionToUnresult = null;
	export let race;

	let displayScratchButton = false;
	let displayUnscratchButton = false;
	const dispatch = createEventDispatcher();

	$: winResult =
		(selection.liabilities?.win &&
			selection.liabilities.win.result + (includeMulti ? selection.liabilities.win.multi_result : 0)) ||
		0;

	$: cashoutResult =
		(selection.liabilities?.cashout &&
			selection.liabilities.cashout.result + (includeMulti ? selection.liabilities.cashout.multi_result : 0)) ||
		0;

	$: winTakeOut =
		(selection.liabilities?.win && selection.liabilities.win.take_out + (includeMulti ? winMultiTakeOut : 0)) || 0;

	$: winHold = (selection.liabilities?.win && selection.liabilities.win.hold + (includeMulti ? winMultiHold : 0)) || 0;

	$: winMultiTakeOut = selection.liabilities?.win?.multi_take_out || 0;
	$: winMultiHold = selection.liabilities?.win?.multi_hold || 0;

	$: cashoutHold =
		(selection.liabilities?.cashout && selection.liabilities.cashout.hold + (includeMulti ? cashoutMultiHold : 0)) || 0;
	$: cashoutMultiHold = selection.liabilities?.cashout?.multi_hold || 0;

	$: placeTakeOut =
		(selection.liabilities?.place && selection.liabilities.place.take_out + (includeMulti ? placeMultiTakeOut : 0)) ||
		0;
	$: placeHold =
		(selection.liabilities?.place && selection.liabilities.place.hold + (includeMulti ? placeMultiHold : 0)) || 0;
	$: placeMultiTakeOut = selection.liabilities?.place?.multi_take_out || 0;
	$: placeMultiHold = selection.liabilities?.place?.multi_hold || 0;

	$: avgDiv = winTakeOut && winHold ? (winTakeOut / winHold).toFixed(2) : 0;
	$: hasMulti = winMultiHold || placeMultiHold;
	$: raceWinHold = $RaceStore && ($RaceStore.where('id', selection.race_id).pluck('liabilities.win_hold').first() || 0);
	$: canBeUnresult =
		race &&
		dayjs().diff(dayjs(race.start_time), 'hour') < 24 &&
		[EventStatus.Closed, EventStatus.Interim, EventStatus.Paying, EventStatus.Paid].includes(race.status.id); //The race is within 24 hours of starting

	function totePrice(tote) {
		let totePrice = $PriceStore.where('selection_id', selection.id).firstWhere('product.id', tote);
		return totePrice ? NumberFormat(totePrice.price, false, false) : '-';
	}

	function price(betType) {
		if (previewDivisor && betType === BetType.Place) {
			return PriceStore.calcDivisorPlacePrice(selection.id, divisor);
		} else {
			let fixedPrice = $PriceStore
				.where('selection_id', selection.id)
				.where('product.id', Products.Fixed)
				.firstWhere('bet_type.id', betType);
			return fixedPrice ? NumberFormat(fixedPrice.price, false, false) : '-';
		}
	}

	function onSelectionClick() {
		showBetsForSelectionId.set($showBetsForSelectionId === selection.id ? false : selection.id);
	}

	function onScratchSelectionClick() {
		selectionToScratch = selection;
		dispatch('scratch');
	}

	function onUnscratchSelectionClick() {
		selectionToUnscratch = selection;
		dispatch('unscratch');
	}

	function openUpdateSelectionPrompt() {
		selectionToUpdate = selection;
		dispatch('updateSelection');
	}

	function openSelectionUnresultPrompt() {
		selectionToUnresult = selection;
		dispatch('unresultSelection');
	}

	function showHoverButtons() {
		if (
			[EventStatus.Open, EventStatus.Closed, EventStatus.ReOpened].includes(race.status.id) &&
			!race.unfinalised &&
			displayScratchButton === false
		) {
			displayScratchButton = true;
		}
		if (
			[EventStatus.Open, EventStatus.ReOpened].includes(race.status.id) &&
			selection.status.id !== SelectionStatus.Active
		) {
			displayUnscratchButton = true;
		}
	}
</script>

<ul
	class="grid racing-selection-grid py-1 cursor-pointer hover:bg-gray-200 {selection?.status?.id &&
	selection.status.id !== SelectionStatus.Active
		? 'opacity-75'
		: ''}"
	on:click={onSelectionClick}
	on:mouseenter={showHoverButtons}
	on:mouseleave={() => {
		displayScratchButton = false;
		displayUnscratchButton = false;
	}}
>
	<li class="flex my-auto text-xs text-center">
		<!--Prices-->
		<div class="w-10 p-1">
			<AnimateChange value={$PriceStore && totePrice(Products.NSW)} />
		</div>
		<div class="w-10 p-1 mx-1 border-l border-r">
			<AnimateChange value={$PriceStore && totePrice(Products.SUP)} />
		</div>
		<div class="w-10 p-1 border-r">
			<AnimateChange value={$PriceStore && totePrice(Products.TATTS)} />
		</div>
	</li>
	<li class="my-auto text-xs text-center px-1">
		<OfficialFlucs flucs={selection.display_price_flucs} />
	</li>
	<li class="flex items-center my-auto text-xs text-center">
		<div class="flex">
			<div class="mr-1">
				<PriceRoll {selection} betType={BetType.Win} />
			</div>
			<div class="w-12 p-1 mr-1 border">
				<AnimateChange value={$PriceStore && price(BetType.Win)} />
			</div>
			<div class="mr-1">
				<PriceRoll {selection} betType={BetType.Place} />
			</div>
			<div class="w-12 p-1 mr-1 border {notValidDivisor ? 'border-red-500' : ''}">
				<AnimateChange value={($PriceStore || previewDivisor || divisor) && price(BetType.Place)} />
			</div>
		</div>
	</li>
	<li class="flex flex-col my-auto">
		<div class="flex flex-row justify-between items-center">
			<div class="flex flex-row justify-between items-center w-full">
				<div
					class:font-semibold={favourite && favourite.selection_id === selection.id}
					class:line-through={selection.status.id !== SelectionStatus.Active}
					class:text-xs={selection.status.id !== SelectionStatus.Active}
					class:text-sm={selection.status.id === SelectionStatus.Active}
					class="truncate"
				>
					<div class="">
						<span>{selection.number}. {selection.name}</span>
					</div>
				</div>
				{#if hasMulti}
					<div class="ml-1 -mt-1 font-bold text-blue-500" title="Has Multi">*</div>
				{/if}
				{#if favourite && favourite.selection_id === selection.id}
					<div class="ml-1 -mt-1 font-bold text-red-500" title="Favourite">*</div>
				{/if}
			</div>
			<span class="mr-1 font-semibold">{selection.position ? getGetOrdinal(selection.position) : ''}</span>
		</div>
		{#if selection.status.id !== SelectionStatus.Active}
			<div class="text-xs text-left whitespace-nowrap">
				{selection.scratched_time ? '@' + dayjs(selection.scratched_time).format('HH:mm D/MM') : 'N/A'}
				{#if selection.deductions}
					({selection.deductions.win ? `W:${selection.deductions.win}c,` : ''}
					{selection.deductions.place ? `P:${selection.deductions.place}c` : ''})
				{/if}
				{#if selection.status.id === SelectionStatus.LateScratching}
					<span class="px-1 ml-1 font-semibold bg-yellow-500 rounded-sm text-dark-grey text-tiny"> LATE </span>
				{/if}
			</div>
		{/if}
	</li>
	<li
		class="flex flex-row justify-between items-center space-x-1"
		class:justify-between={selection.status.id == SelectionStatus.Active}
		class:justify-end={selection.status.id !== SelectionStatus.Active}
	>
		{#if selection.status.id == SelectionStatus.Active}
			<button
				on:click|stopPropagation={openUpdateSelectionPrompt}
				title="Update selection [Starting price, Top flucs]"
				class="py-0.5 px-0.5 flex items-center justify-center w-4.5 h-4.5 text-white bg-blue-600 hover:bg-grey focus:outline-none flex items-center"
			>
				<Icon name="edit" />
			</button>
			{#if race.status && [EventStatus.Open, EventStatus.ReOpened, EventStatus.Abandoned].indexOf(race.status.id) === -1 && !race.unfinalised && canBeUnresult}
				<button
					on:click|stopPropagation={openSelectionUnresultPrompt}
					title="Unfinalise selection"
					class="p-1 text-white bg-yellow hover:bg-grey focus:outline-none flex items-center w-4.5 h-4.5"
				>
					<Icon name="unfinalise" className="" />
				</button>
			{/if}
			{#if displayScratchButton}
				<button
					on:click|stopPropagation={onScratchSelectionClick}
					title="Scratch horse from race"
					class="p-1 text-white bg-danger hover:bg-grey focus:outline-none flex items-center w-4.5 h-4.5"
				>
					<Icon name="cross" />
				</button>
			{/if}
		{/if}

		{#if displayUnscratchButton}
			<button
				on:click|stopPropagation={onUnscratchSelectionClick}
				title="Unscratch horse from race"
				class="p-1 text-white bg-warning hover:bg-grey focus:outline-none flex items-center"
			>
				<Icon name="unfinalise" classes="text-tiny block w-2.5 h-2.5 opacity-100 inline pointer-events-none" />
			</button>
		{/if}
	</li>
	<!-- LIABILITIES -->
	<li class="my-auto text-center {winResult < 0 ? 'text-red-500 font-semibold' : ''}">
		{(winResult && NumberFormat(winResult, false, false)) ||
			(selection.status.id === SelectionStatus.Active && raceWinHold && NumberFormat(raceWinHold, false, false)) ||
			'-'}
	</li>
	<li class="my-auto text-center {cashoutResult < 0 ? 'text-red-500 font-semibold' : ''}">
		{(cashoutResult && NumberFormat(cashoutResult, false, false)) || '-'}
	</li>
	<li class="my-auto text-center">
		{(winTakeOut && NumberFormat(winTakeOut, false, false)) || '-'}
	</li>
	<li class="my-auto text-center">
		{(placeTakeOut && NumberFormat(placeTakeOut, false, false)) || '-'}
	</li>
	<li class="my-auto text-center">{(avgDiv && avgDiv) || '-'}</li>
	<li class="my-auto text-center">
		{(winHold && NumberFormat(winHold, false, false)) || '-'}
	</li>
	<li class="my-auto text-center">
		{(placeHold && NumberFormat(placeHold, false, false)) || '-'}
	</li>
	<li class="my-auto text-center">
		{(cashoutHold && NumberFormat(cashoutHold, false, false)) || '-'}
	</li>
	{#if $showBetsForSelectionId === selection.id}
		<SelectionBets {selection} />
	{/if}
</ul>

<style>
	.racing-selection-grid {
		grid-template-columns: 1fr 1.2fr 1.5fr 1.5fr 0.3fr repeat(8, minmax(0, 0.85fr));
	}
</style>
